import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/react';
import type { Session } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentSession, onAuthStateChange } from '@/lib/supabaseAdapter';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const posthog = usePostHog();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const identifyUser = (session: Session | null) => {
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
        posthog?.identify(session.user.id);
      } else {
        Sentry.setUser(null);
        posthog?.reset();
      }
    };

    const {
      data: { subscription },
    } = onAuthStateChange(async (_event, session: Session | null) => {
      setSession(session);
      identifyUser(session);
      setIsLoading(false);
    });

    getCurrentSession().then((session) => {
      const s = session as Session | null;
      setSession(s);
      identifyUser(s);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [posthog?.identify, posthog?.reset]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    // Redirect to login with the return path
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
